import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Input, Modal, Select, Textarea, useToast } from "@theg1group/ui";
import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import type { PartnerInput, PartnerRow, PartnerStatus } from "../lib/types.ts";

const STATUS_OPTIONS: Array<{ value: PartnerStatus; label: string }> = [
	{ value: "active", label: "Active" },
	{ value: "paused", label: "Paused" },
	{ value: "terminated", label: "Terminated" },
	{ value: "terminated_fraud", label: "Terminated (fraud)" },
];

/** Create (no `partner`) or edit (with `partner`) modal. */
export function PartnerForm({
	open,
	onClose,
	partner,
	onSaved,
	nameLocked = false,
}: {
	open: boolean;
	onClose: () => void;
	partner?: PartnerRow | null;
	onSaved?: (p: PartnerRow) => void;
	/** Name/externalRef are managed by the Affiliates sync — render them read-only. */
	nameLocked?: boolean;
}) {
	const qc = useQueryClient();
	const toast = useToast();
	const [name, setName] = useState("");
	const [status, setStatus] = useState<PartnerStatus>("active");
	const [externalRef, setExternalRef] = useState("");
	const [notes, setNotes] = useState("");

	useEffect(() => {
		if (!open) return;
		setName(partner?.name ?? "");
		setStatus(partner?.status ?? "active");
		setExternalRef(partner?.externalRef ?? "");
		setNotes(partner?.notes ?? "");
	}, [open, partner]);

	const save = useMutation({
		mutationFn: (body: PartnerInput | Pick<PartnerInput, "status" | "notes">) =>
			partner
				? api.patch<PartnerRow>(`/api/partners/${partner.id}`, body)
				: api.post<PartnerRow>("/api/partners", body),
		onSuccess: (p) => {
			toast.addToast("success", partner ? "Partner updated" : "Partner created");
			qc.invalidateQueries({ queryKey: ["partners"] });
			if (partner) qc.invalidateQueries({ queryKey: ["partner", partner.id] });
			onSaved?.(p);
			onClose();
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={partner ? "Edit partner" : "New partner"}
			footer={
				<div className="flex justify-end gap-2">
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="primary"
						disabled={!name.trim()}
						loading={save.isPending}
						onClick={() =>
							save.mutate(
								// PATCH rejects name/externalRef changes for synced partners (managed_by_sync).
								nameLocked
									? { status, notes: notes.trim() || null }
									: {
											name: name.trim(),
											status,
											externalRef: externalRef.trim() || null,
											notes: notes.trim() || null,
										},
							)
						}
					>
						{partner ? "Save" : "Create"}
					</Button>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<FormField
					label="Name"
					htmlFor="pf-name"
					required
					hint={nameLocked ? "managed by Affiliates sync" : undefined}
				>
					<Input
						id="pf-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Partner / affiliate name"
						disabled={nameLocked}
					/>
				</FormField>
				<FormField label="Status" htmlFor="pf-status">
					<Select
						id="pf-status"
						value={status}
						onChange={(e) => setStatus(e.target.value as PartnerStatus)}
						options={STATUS_OPTIONS}
					/>
				</FormField>
				<FormField
					label="External ref"
					htmlFor="pf-ref"
					hint={nameLocked ? "managed by Affiliates sync" : "Affiliate id / CRM key — free text"}
				>
					<Input
						id="pf-ref"
						value={externalRef}
						onChange={(e) => setExternalRef(e.target.value)}
						disabled={nameLocked}
					/>
				</FormField>
				<FormField label="Notes" htmlFor="pf-notes">
					<Textarea
						id="pf-notes"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						className="min-h-[90px]"
					/>
				</FormField>
			</div>
		</Modal>
	);
}
